import type { UtilityContext } from '@change-corgi/core-react/utilityContext';

import { shouldShowSharingGoalFlow } from '#src/app/pages/psf/share/shared/utils';
import { treatExperiment } from '#src/app/shared/api/experiments';
import { isMobile } from '#src/app/shared/utils/userAgent';

import type { PsfShareDataQuery } from '../api.graphql';

type Experiment = PsfShareDataQuery['experiments']['psfShareGoalUxExperiment'];

type Params = {
	experiment: Experiment;
	utilityContext: UtilityContext;
	skip: boolean;
	isSuperRecruiterFlowEnabled: boolean;
	isShareSlideUpEnabled: boolean;
};

/**
 * Treats the Share Goal UX experiment on psf/share for eligible users and returns the resolved
 * experiment object with `treated` reflecting whether the treat mutation actually succeeded (not just
 * whether it was attempted). Eligibility reuses the shared `shouldShowSharingGoalFlow` predicate —
 * the same one `useShouldShowSharingGoalFlow` uses for rendering — plus `isEnabledByFC`, and the
 * server-safe `isMobile(utilityContext)` in place of the client-only `useIsMobile()` hook.
 *
 * The experiment is fetched with `treat: false` (see api.graphql) so bucketing is only registered —
 * and `$exposure` only fires (from PageBody, via `useTrackExperimentExposureOnMount`) — when the
 * experiment actually runs.
 */
export async function normalizePsfShareGoalUxExperiment(params: Params): Promise<Experiment> {
	const { experiment, utilityContext } = params;

	if (!shouldRunExperiment(params)) {
		return { ...experiment, variation: 'control', treated: false };
	}

	if (experiment.override) {
		// A QA/dev `?exo.` override already forced `variation`/`override: true` upstream, and
		// `useTrackExperimentExposure` already no-ops `$exposure` for `override`. Pass it through
		// unchanged instead of calling `treatExperiment`: that hits the real Experiment Service treat
		// endpoint for the session UUID, which would register a real bucket assignment for a visit
		// that was never a genuine assignment — polluting `experiment_variations` for that user.
		return experiment;
	}

	// `treated` mirrors whether the treat mutation actually succeeded — if the Experiment Service
	// call fails (outage, network error), `treatExperiment` swallows the error and resolves false,
	// so `treated` stays false and `useTrackExperimentExposure` won't fire `$exposure` for an
	// assignment that was never registered in `experiment_variations`.
	const treated = await treatExperiment(utilityContext, experiment.experimentName);

	return { ...experiment, treated };
}

function shouldRunExperiment({
	experiment,
	utilityContext,
	skip,
	isSuperRecruiterFlowEnabled,
	isShareSlideUpEnabled,
}: Params): boolean {
	return (
		isEnabledByFC(experiment) &&
		shouldShowSharingGoalFlow({
			skip,
			isSuperRecruiterFlowEnabled,
			isShareSlideUpEnabled,
			isMobile: isMobile(utilityContext),
		})
	);
}

/** An `enabledIf` failure (FC disabled, fetch error, not registered yet, …) surfaces as `fallback: true`. */
function isEnabledByFC(experiment: Experiment): boolean {
	return !experiment.fallback;
}
